(() => {
  if (customElements.get('bf-showcase')) return;
  class BFShowcase extends window.BFOfferElement {
    connectedCallback() {
      super.connectedCallback();
      const { signal } = this.lifecycle;
      this.stack = this.querySelector('[data-stack]');
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.progress = this.querySelector('[data-progress]');
      this.media = window.matchMedia('(min-width: 901px) and (min-height: 700px) and (prefers-reduced-motion: no-preference)');
      this.mobileMedia = window.matchMedia('(max-width: 900px) and (prefers-reduced-motion: no-preference)');
      window.addEventListener('scroll', () => this.scheduleProgress(), { passive: true, signal });
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.layout(), 120);
      }, { passive: true, signal });
      this.media.addEventListener('change', () => this.layout(), { signal });
      this.mobileMedia.addEventListener('change', () => this.layout(), { signal });
      this.stack.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal });
      this.addEventListener('keydown', (event) => {
        const carousel = event.target.closest('scroll-carousel');
        if (!carousel || event.target !== carousel || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const instant = !this.media.matches;
        const actions = {
          ArrowLeft: () => carousel.previous({ instant }),
          ArrowRight: () => carousel.next({ instant }),
          Home: () => carousel.select(0, { instant }),
          End: () => carousel.select(carousel.cells.length - 1, { instant })
        };
        if (!actions[event.key] || !carousel.cells?.length) return;
        event.preventDefault();
        actions[event.key]();
      }, { signal });
      // The native header updates CSS variables when its size or visibility changes.
      this.headerObserver = new MutationObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.layout(), 120);
      });
      this.headerObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-panel-nav]');
        if (button) this.showPanel(Number(button.dataset.panelNav));
      }, { signal });
      this.addEventListener('focusin', (event) => {
        const panel = event.target.closest('[data-panel]');
        if (panel && event.target.matches(':focus-visible') && this.classList.contains('is-stacked')) this.showPanel(this.panels.indexOf(panel), false);
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.panels.findIndex((panel) => panel.dataset.blockId === event.detail?.blockId);
        if (index !== -1) this.showPanel(index, false);
      }, { signal });
      this.panelObserver = new ResizeObserver(() => this.layout());
      this.panels.forEach((panel) => this.panelObserver.observe(panel));
      this.layout();
      document.fonts?.ready.then(() => { if (!signal.aborted) this.layout(); });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.progressFrame);
      clearTimeout(this.resizeTimer);
      clearTimeout(this.gestureTimer);
      this.headerObserver?.disconnect();
      this.panelObserver?.disconnect();
      super.disconnectedCallback();
    }

    onClick(event) {
      // Native drag scrolling cancels the following click. Respect that cancellation
      // before the shared Apply handler can treat it as an intentional button click.
      if (!event.defaultPrevented) super.onClick(event);
    }

    layout() {
      const sticky = this.dataset.sticky === 'true' && this.dataset.editor !== 'true' && this.panels.length > 1;
      const enabled = sticky && this.media.matches;
      this.classList.toggle('is-stacked', enabled);
      const mobile = sticky && this.mobileMedia.matches;
      this.classList.toggle('is-mobile-stacked', mobile);
      // Tall mobile cards scroll fully before their bottom rests in the viewport.
      // Observe actual heights so late images and merchant copy keep the pin accurate.
      this.panels.forEach((panel) => {
        if (mobile) {
          const height = `${panel.getBoundingClientRect().height}px`;
          if (panel.style.getPropertyValue('--bf-panel-height') !== height) panel.style.setProperty('--bf-panel-height', height);
        } else panel.style.removeProperty('--bf-panel-height');
      });
      // Long merchant copy or large text must remain reachable instead of clipping below the viewport.
      if (enabled && this.panels.some((panel) => {
        const copy = panel.querySelector('.bf-showcase__copy');
        return copy.scrollHeight > panel.querySelector('.bf-showcase__grid').clientHeight + 2;
      })) this.classList.remove('is-stacked');
      this.updateProgress();
    }

    targets() {
      const start = this.stack.getBoundingClientRect().top + window.scrollY;
      const firstStyle = getComputedStyle(this.panels[0]);
      const top = this.classList.contains('is-stacked') ? parseFloat(firstStyle.top) || 0 :
        (parseFloat(firstStyle.scrollMarginTop) || 0) + (parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0);
      let offset = 0;
      return this.panels.map((panel) => {
        const target = start + offset - top;
        offset += panel.getBoundingClientRect().height;
        return Math.max(0, target);
      });
    }

    showPanel(index, smooth = true) {
      if (!this.panels[index]) return;
      window.scrollTo({ top: this.targets()[index], behavior: smooth && this.media.matches ? 'smooth' : 'instant' });
    }

    onWheel(event) {
      if (!this.classList.contains('is-stacked') || event.defaultPrevented || event.cancelable === false || event.ctrlKey || event.metaKey || event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY) ||
        event.target.closest('input,textarea,select,[contenteditable="true"],dialog,[role="dialog"]')) return;
      const targets = this.targets();
      // Before arrival and after the last collection, the page keeps normal scrolling.
      if (window.scrollY < targets[0] - 80 || window.scrollY > targets.at(-1) + 80) return;
      const direction = Math.sign(event.deltaY);
      if (!direction) return;
      if (this.wheelDirection && direction !== this.wheelDirection) {
        this.wheelTotal = 0;
        this.wheelLockUntil = 0;
        // Stop a pending forward animation before responding to a reverse gesture.
        window.scrollTo({ top: window.scrollY, behavior: 'instant' });
      }
      this.wheelDirection = direction;
      // Pick the next target in the requested direction, including during an animation.
      let next = direction > 0 ? targets.findIndex((target) => target > window.scrollY + 3) : -1;
      if (direction < 0) {
        for (let index = targets.length - 1; index >= 0; index--) {
          if (targets[index] < window.scrollY - 3) { next = index; break; }
        }
      }
      if (next === -1) {
        this.wheelTotal = 0;
        this.wheelLockUntil = 0;
        return;
      }
      event.preventDefault();
      clearTimeout(this.gestureTimer);
      this.gestureTimer = setTimeout(() => { this.wheelTotal = 0; }, 190);
      // Only a completed threshold starts this cooldown; continued input cannot extend it.
      if (performance.now() < (this.wheelLockUntil || 0)) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      this.wheelTotal = (this.wheelTotal || 0) + event.deltaY * unit;
      if (Math.abs(this.wheelTotal) < 28) return;
      this.wheelTotal = 0;
      this.wheelLockUntil = performance.now() + 580;
      this.showPanel(next);
    }

    scheduleProgress() {
      if (!this.progressFrame) this.progressFrame = requestAnimationFrame(() => this.updateProgress());
    }

    updateProgress() {
      this.progressFrame = 0;
      if (!this.progress || !this.panels.length) return;
      const rect = this.stack.getBoundingClientRect();
      this.progress.hidden = !this.classList.contains('is-stacked') || rect.top > window.innerHeight * .6 || rect.bottom < window.innerHeight * .5;
      const targets = this.targets();
      let active = 0;
      targets.forEach((target, index) => { if (window.scrollY >= target - 40) active = index; });
      this.progress.querySelectorAll('button').forEach((button, index) => {
        if (index === active) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
    }
  }
  customElements.define('bf-showcase', BFShowcase);
})();
